import { CSSProperties, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import type { DropdownProps } from 'antd';
import { Col, Dropdown, Menu, Row } from 'antd';
import { TbWorldPin } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    AeSVGIcon,
    ChSVGIcon,
    CnSVGIcon,
    DeSVGIcon,
    FrSVGIcon,
    GbSVGIcon,
    HkSVGIcon,
    IeSVGIcon,
    JpSVGIcon,
    KrSVGIcon,
    SgSVGIcon,
    UsSVGIcon,
} from '@/layouts/02-Authenticated/layout/components/CommandPalette/layout/01-Content/99-Right/svgs';
import {
    BaseSkeleton,
    DropdownMenuLabel,
    DropdownOptionAvatar,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    FLOATING_DROPDOWN_OVERLAY_STYLE,
} from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';
import {
    buildRecommendedTimezones,
    FINANCIAL_HUB_TIMEZONES,
    formatTimezoneCurrentTime,
    formatTimezoneLabel,
    formatTimezoneRegion,
    getBrowserTimezone,
    getSupportedTimezones,
} from '@/utilities';

const MAX_RENDERED_TIMEZONES = 80;
const DROPDOWN_LIST_HEIGHT = 360;

const timeZoneToSVGIconMap = {
    'America/New_York': UsSVGIcon,
    'Europe/Dublin': IeSVGIcon,
    'Europe/London': GbSVGIcon,
    'Europe/Paris': FrSVGIcon,
    'Europe/Zurich': ChSVGIcon,
    'Europe/Berlin': DeSVGIcon,
    'Asia/Dubai': AeSVGIcon,
    'Asia/Hong_Kong': HkSVGIcon,
    'Asia/Singapore': SgSVGIcon,
    'Asia/Shanghai': CnSVGIcon,
    'Asia/Seoul': KrSVGIcon,
    'Asia/Tokyo': JpSVGIcon,
} as Record<string, string>;

interface TimezoneDropdownProps {
    disabled?: boolean;
    onChange?: (timeZone: string) => Promise<void> | void;
    placeholder?: string;
    placement?: DropdownProps['placement'];
    size?: 'small' | 'middle' | 'large';
    value?: string | null;
}

const renderTimezoneIcon = (timeZone: string): ReactNode => {
    const src = timeZoneToSVGIconMap[timeZone];

    if (!src) {
        return <TbWorldPin size='1rem' />;
    }

    return (
        <img alt={timeZone} src={src} style={{ display: 'block', height: '100%', objectFit: 'cover', width: '100%' }} />
    );
};

const TimezoneDropdown: FC<TimezoneDropdownProps> = ({
    disabled,
    onChange,
    placeholder = 'Select Timezone...',
    placement = 'bottomLeft',
    size,
    value,
}): JSX.Element => {
    const { selectedUser: user, updateUser } = useUserStore();
    const { token } = useAntDesignTheme();

    const [query, setQuery] = useState('');
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [renderedTimezoneCount, setRenderedTimezoneCount] = useState(MAX_RENDERED_TIMEZONES);
    const selectedTimezone = value !== undefined ? (value ?? undefined) : user?.timeZone;

    const getTimezoneAvatarStyle = useCallback(
        (timeZone: string): CSSProperties | undefined => {
            if (!timeZoneToSVGIconMap[timeZone]) {
                return undefined;
            }

            const isSquareFlag = timeZone === 'Europe/Zurich';

            return {
                borderRadius: token.borderRadius,
                height: '1.25rem',
                minWidth: isSquareFlag ? '1.25rem' : '1.875rem',
                overflow: 'hidden',
                width: isSquareFlag ? '1.25rem' : '1.875rem',
            };
        },
        [token.borderRadius],
    );

    const browserTimezone = useMemo(() => getBrowserTimezone(), []);
    const supportedTimezones = useMemo(() => getSupportedTimezones(), []);
    const recommendedTimezones = useMemo(
        () => buildRecommendedTimezones({ browserTimezone, preferredTimezone: selectedTimezone ?? undefined }),
        [browserTimezone, selectedTimezone],
    );
    const recommendedTimezoneKeys = useMemo(
        () => new Set(recommendedTimezones.map(({ timeZone }) => timeZone)),
        [recommendedTimezones],
    );
    const normalizedQuery = query.trim().toLowerCase();
    const matchesQuery = useCallback(
        (timeZone: string, label?: string) => {
            if (!normalizedQuery) {
                return true;
            }

            return (
                timeZone.toLowerCase().includes(normalizedQuery) ||
                formatTimezoneLabel(timeZone).toLowerCase().includes(normalizedQuery) ||
                formatTimezoneRegion(timeZone).toLowerCase().includes(normalizedQuery) ||
                Boolean(label?.toLowerCase().includes(normalizedQuery))
            );
        },
        [normalizedQuery],
    );

    const filteredRecommendedTimezones = useMemo(
        () => recommendedTimezones.filter(({ city, label, timeZone }) => matchesQuery(timeZone, `${label} ${city}`)),
        [matchesQuery, recommendedTimezones],
    );

    const matchingTimezones = useMemo(() => {
        return supportedTimezones
            .filter((timeZone) => !recommendedTimezoneKeys.has(timeZone))
            .filter((timeZone) => matchesQuery(timeZone));
    }, [matchesQuery, recommendedTimezoneKeys, supportedTimezones]);
    const filteredTimezones = useMemo(
        () => matchingTimezones.slice(0, renderedTimezoneCount),
        [matchingTimezones, renderedTimezoneCount],
    );

    useEffect(() => {
        setRenderedTimezoneCount(MAX_RENDERED_TIMEZONES);
    }, [normalizedQuery]);

    const handleTimezoneSelect = async (timeZone: string) => {
        if (!timeZone) {
            return;
        }

        if (timeZone === selectedTimezone) {
            setOpen(false);

            return;
        }

        setSaving(true);

        try {
            if (onChange) {
                await onChange(timeZone);
                setOpen(false);

                return;
            }

            if (!user?.id) {
                return;
            }

            const updatedUser = await updateUser({ timeZone });

            toast('Timezone Updated', {
                icon: <TbWorldPin size='1rem' />,
                description: updatedUser?.timeZone ?? timeZone,
            });

            setOpen(false);
        } finally {
            setSaving(false);
        }
    };

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 32;

        if (!nearBottom || renderedTimezoneCount >= matchingTimezones.length) {
            return;
        }

        setRenderedTimezoneCount((count) => Math.min(count + MAX_RENDERED_TIMEZONES, matchingTimezones.length));
    };

    const renderTimezoneMenuItem = (timeZone: string, options?: { label?: string }) => {
        const metadata = [
            options?.label ? formatTimezoneLabel(timeZone) : null,
            formatTimezoneRegion(timeZone),
            formatTimezoneCurrentTime(timeZone),
        ]
            .filter(Boolean)
            .join(' · ');

        return (
            <Menu.Item key={timeZone} title={null} onClick={() => handleTimezoneSelect(timeZone)}>
                <DropdownOptionContent
                    avatar={{ icon: renderTimezoneIcon(timeZone) }}
                    avatarStyle={getTimezoneAvatarStyle(timeZone)}
                    metadata={[metadata]}
                    selected={selectedTimezone === timeZone}
                    singleLine
                    title={options?.label ?? formatTimezoneLabel(timeZone)}
                />
            </Menu.Item>
        );
    };

    const buttonLabel = selectedTimezone ? formatTimezoneLabel(selectedTimezone) : placeholder;

    return (
        <Dropdown
            disabled={disabled}
            open={open}
            overlayStyle={FLOATING_DROPDOWN_OVERLAY_STYLE}
            placement={placement}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);

                if (!nextOpen) {
                    setQuery('');
                }
            }}
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    contentProps={{ onScroll: handleScroll }}
                    contentStyle={{ overflowX: 'hidden', overflowY: 'auto' }}
                    maxHeight={DROPDOWN_LIST_HEIGHT}
                    search={{
                        placeholder: 'Search Timezones...',
                        setValue: setQuery,
                        value: query,
                    }}
                    width={360}
                >
                    {filteredRecommendedTimezones.length > 0 || filteredTimezones.length > 0 ? (
                        <DropdownPanelMenu
                            selectable={false}
                            selectedKeys={selectedTimezone ? [selectedTimezone] : []}
                            style={{ maxHeight: 'none', overflow: 'visible' }}
                        >
                            {filteredRecommendedTimezones.length > 0 ? (
                                <Menu.ItemGroup
                                    key='recommended-timezones'
                                    title={<DropdownMenuLabel style={{ padding: 0 }}>Recommended</DropdownMenuLabel>}
                                >
                                    {filteredRecommendedTimezones.map(({ label, timeZone }) =>
                                        renderTimezoneMenuItem(timeZone, {
                                            label:
                                                label === 'Preferred' || label === 'Local'
                                                    ? label
                                                    : FINANCIAL_HUB_TIMEZONES.find((hub) => hub.timeZone === timeZone)
                                                          ?.label,
                                        }),
                                    )}
                                </Menu.ItemGroup>
                            ) : null}
                            {filteredTimezones.length > 0 ? (
                                <Menu.ItemGroup
                                    key='all-timezones'
                                    title={
                                        <DropdownMenuLabel style={{ padding: 0 }}>
                                            {query ? 'Matching Timezones' : 'All Timezones'}
                                        </DropdownMenuLabel>
                                    }
                                >
                                    {filteredTimezones.map((timeZone) => renderTimezoneMenuItem(timeZone))}
                                    {renderedTimezoneCount < matchingTimezones.length && (
                                        <Menu.Item key='limit-hint' disabled>
                                            <BaseSkeleton title paragraph={{ rows: 2 }} />
                                        </Menu.Item>
                                    )}
                                    {renderedTimezoneCount >= matchingTimezones.length && (
                                        <Menu.Item key='end-of-list' disabled>
                                            <small style={{ color: token.colorTextTertiary }}>End of List</small>
                                        </Menu.Item>
                                    )}
                                </Menu.ItemGroup>
                            ) : null}
                        </DropdownPanelMenu>
                    ) : (
                        <EmptySection
                            height={16 * 10}
                            icon={<TbWorldPin size='75%' style={{ color: token.colorTextTertiary }} />}
                            label='No Matching Timezones'
                            description={
                                query ? (
                                    <>
                                        No timezones match <strong>{query}</strong>.
                                    </>
                                ) : (
                                    'No timezones are available.'
                                )
                            }
                        />
                    )}
                </DropdownPanel>
            )}
        >
            <DropdownButton
                loading={saving}
                block
                disabled={disabled}
                size={size}
                style={{
                    justifyContent: 'flex-start',
                }}
            >
                <Row align='middle' wrap={false} style={{ width: '100%' }}>
                    <Col flex='none'>
                        {selectedTimezone ? (
                            <DropdownOptionAvatar
                                icon={renderTimezoneIcon(selectedTimezone)}
                                style={{ ...getTimezoneAvatarStyle(selectedTimezone), marginRight: '0.5rem' }}
                            />
                        ) : null}
                    </Col>
                    <Col flex='auto' style={{ textAlign: 'left' }}>
                        {!selectedTimezone && (
                            <TbWorldPin size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }} />
                        )}
                        {buttonLabel}
                    </Col>
                </Row>
            </DropdownButton>
        </Dropdown>
    );
};

export default TimezoneDropdown;
